import React from 'react';
import { FiArrowLeft, FiArrowRight, FiSave, FiX } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const WizardShell = ({
  title,
  subtitle,
  steps = [],
  activeStep = 0,
  canContinue = true,
  isSaving = false,
  onBack,
  onNext,
  onSaveDraft,
  onClose,
  children,
  primaryLabel,
}) => {
  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;
  const progressValue = steps.length > 1 ? ((activeStep + 1) / steps.length) * 100 : 100;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-bold uppercase tracking-wider text-[#0A84FF]">Bundling Wizard</p>
            <h2 className="text-xl font-extrabold tracking-tight text-label-primary md:text-2xl">{title}</h2>
            {subtitle ? <p className="text-sm leading-relaxed text-label-secondary">{subtitle}</p> : null}
          </div>
          <Button variant="ghost" className="gap-2" onClick={onClose}>
            <FiX className="h-4 w-4" />
            Exit
          </Button>
        </div>

        <div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${progressValue}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {steps.map((step, index) => {
              const isCurrent = index === activeStep;
              const isDone = index < activeStep;
              return (
                <Badge
                  key={step.key || step.label}
                  variant="outline"
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-bold normal-case',
                    isCurrent && 'border-[#B8DAFF] bg-[#E8F2FF] text-[#0A84FF]',
                    isDone && !isCurrent && 'border-green-200 bg-green-50 text-green-700',
                    !isCurrent && !isDone && 'border-slate-200 bg-slate-50 text-slate-600'
                  )}
                >
                  {index + 1}. {step.label}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[rgba(15,23,42,0.1)] bg-surface-grouped px-4 py-4 shadow-primeSm md:px-6 md:py-5">
        {children}
      </div>

      <div className="sticky bottom-0 rounded-md border border-[rgba(15,23,42,0.1)] bg-surface-grouped px-3 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" disabled={isFirst || isSaving} className="gap-2" onClick={onBack}>
            <FiArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" disabled={isSaving} className="gap-2" onClick={onSaveDraft}>
              <FiSave className="h-4 w-4" />
              Save Draft
            </Button>
            <Button
              disabled={!canContinue || isSaving}
              className="gap-2 bg-brand-500 hover:bg-brand-600"
              onClick={onNext}
            >
              {primaryLabel || (isLast ? 'Continue' : 'Next')}
              <FiArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardShell;
